import { ApiError, ConflictError, STAFF, qk, useApi, useApiQuery } from '@legalcrm/api';
import { Button, Drawer, Spinner, Textarea } from '@legalcrm/ui';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AssistantCorner } from './AssistantCorner';
import { OPEN_ASSISTANT_EVENT, useAssistantDoodleHidden } from './assistantVisibility';
import { MascotFace, MascotPortrait, MascotStyles } from './AssistantMascot';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  /** The screens it looked at, shown under an answer. */
  lookedAt?: string[];
  failed?: boolean;
}

interface AssistantAnswer {
  reply: string;
  looked_at: string[];
  stopped_early: boolean;
}

/**
 * The read-only assistant: a button in the corner and a side panel.
 *
 * It can only look things up, with the signed-in person's own access -- the server makes
 * every look-up through the same staff API these screens use. The panel says so before
 * the first question, because somebody who believes it changed a record when it cannot
 * is worse off than somebody who never asked.
 *
 * Absent entirely unless the server says it is switched on. The conversation lives only
 * in this component: closing the panel keeps it, reloading the page clears it.
 */
export function AssistantPanel() {
  const api = useApi();
  const location = useLocation();
  const status = useApiQuery<{ enabled: boolean }>(qk.list('assistant'), STAFF.assistant);

  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [turns, busy]);

  const [doodleHidden] = useAssistantDoodleHidden();

  // The account menu opens the assistant when the corner character is hidden.
  useEffect(() => {
    const openDrawer = () => setOpen(true);
    window.addEventListener(OPEN_ASSISTANT_EVENT, openDrawer);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, openDrawer);
  }, []);

  if (!status.data?.enabled) return null;

  const onMatter = /^\/cases\/[0-9a-f-]{36}/.test(location.pathname);
  const onClient = /^\/clients\/[0-9a-f-]{36}/.test(location.pathname);
  const suggestions = [
    'What needs my attention today?',
    ...(onMatter ? ['Summarise this matter and what is holding it up.'] : []),
    ...(onClient ? ['What is still needed from this client?'] : []),
    'Which leads have not been contacted yet?',
    'Which invoices are overdue?',
  ];

  async function send(question: string) {
    const text = question.trim();
    if (!text || busy) return;
    const history: Turn[] = [...turns, { role: 'user', content: text }];
    setTurns(history);
    setDraft('');
    setBusy(true);
    try {
      const answer = await api.post<AssistantAnswer>(`${STAFF.assistant}/`, {
        // Earlier failed answers are not sent back: they were never the assistant's.
        messages: history
          .filter((turn) => !turn.failed)
          .map(({ role, content }) => ({ role, content })),
        page: location.pathname,
      });
      setTurns([
        ...history,
        { role: 'assistant', content: answer.reply, lookedAt: answer.looked_at },
      ]);
    } catch (error) {
      const message =
        error instanceof ConflictError
          ? error.detail
          : error instanceof ApiError && error.status > 0
            ? 'The assistant could not answer just now. Try again in a moment.'
            : 'Your question could not be sent. Check the connection and try again.';
      setTurns([...history, { role: 'assistant', content: message, failed: true }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <MascotStyles />
      {!doodleHidden && (
        <AssistantCorner
          onOpen={() => setOpen(true)}
          onAsk={(question) => {
            setOpen(true);
            void send(question);
          }}
        />
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        size="md"
        title="Assistant"
        description="Looks things up for you. It can't change anything yet."
        footer={
          <form
            className="flex w-full items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void send(draft);
            }}
          >
            <Textarea
              label="Your question"
              hideLabel
              containerClassName="flex-1"
              rows={2}
              value={draft}
              placeholder="Ask about your leads, clients, matters, tasks or invoices"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends; Shift+Enter is a new line, as in every chat box.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send(draft);
                }
              }}
              className="resize-none"
              disabled={busy}
            />
            <Button type="submit" variant="primary" disabled={busy || !draft.trim()}>
              Ask
            </Button>
          </form>
        }
      >
        <div className="space-y-4" aria-live="polite">
          {turns.length === 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <MascotPortrait className="h-32 w-auto shrink-0" />
                <p className="font-display text-xl font-medium text-ink">
                  How may I help today?
                </p>
              </div>
              <p className="text-sm text-ink-muted">
                It sees exactly what you can see in the CRM, and nothing more. It only reads:
                to change something, it will tell you where to do it.
              </p>
              <div className="flex flex-col items-start gap-2">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => void send(suggestion)}
                    className="rounded-full border border-line bg-surface px-3 py-1.5 text-left text-sm text-ink transition-colors hover:border-primary hover:text-primary"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((turn, index) =>
            turn.role === 'user' ? (
              <div key={index} className="flex justify-end">
                <p className="max-w-[85%] whitespace-pre-wrap rounded-lg rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-ink">
                  {turn.content}
                </p>
              </div>
            ) : (
              <div key={index} className="flex max-w-[92%] items-start gap-2">
                <Avatar />
                <div className="min-w-0">
                  <p
                    className={
                      turn.failed
                        ? 'whitespace-pre-wrap rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger'
                        : 'whitespace-pre-wrap rounded-lg rounded-bl-sm bg-surface-sunken px-3 py-2 text-sm text-ink'
                    }
                  >
                    {turn.content}
                  </p>
                  {turn.lookedAt && turn.lookedAt.length > 0 && (
                    <p className="mt-1 text-xs text-ink-subtle">
                      Looked at: {turn.lookedAt.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            ),
          )}

          {busy && (
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Avatar busy />
              <Spinner size="xs" label={null} /> Looking that up…
            </div>
          )}
          <div ref={endRef} />
        </div>
      </Drawer>
    </>
  );
}

function Avatar({ busy = false }: { busy?: boolean }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-surface">
      <MascotFace busy={busy} className="h-8 w-8 object-cover" />
    </span>
  );
}
